// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface WhiteLabelProspectFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospect?: any;
}

const WhiteLabelProspectForm: React.FC<WhiteLabelProspectFormProps> = ({
  open,
  onClose,
  onSuccess,
  prospect
}) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [nextActionDate, setNextActionDate] = useState<Date>();
  const [tags, setTags] = useState<string[]>(prospect?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [geographicMarkets, setGeographicMarkets] = useState<string[]>(prospect?.geographic_markets || []);
  const [newMarket, setNewMarket] = useState('');

  const [formData, setFormData] = useState({
    organization_name: prospect?.organization_name || '',
    contact_name: prospect?.contact_name || '',
    contact_email: prospect?.contact_email || '',
    contact_phone: prospect?.contact_phone || '',
    organization_type: prospect?.organization_type || '',
    annual_event_volume: prospect?.annual_event_volume || '',
    average_participants_per_event: prospect?.average_participants_per_event || '',
    current_technology: prospect?.current_technology || '',
    pipeline_stage: prospect?.pipeline_stage || 'prospect',
    estimated_value: prospect?.estimated_value || '',
    priority_level: prospect?.priority_level || 'medium',
    technology_readiness: prospect?.technology_readiness || 3,
    competition_analysis: prospect?.competition_analysis || '',
    notes: prospect?.notes || '',
    next_action: prospect?.next_action || ''
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddMarket = () => {
    if (newMarket.trim() && !geographicMarkets.includes(newMarket.trim())) {
      setGeographicMarkets([...geographicMarkets, newMarket.trim()]);
      setNewMarket('');
    }
  };

  const handleRemoveMarket = (marketToRemove: string) => {
    setGeographicMarkets(geographicMarkets.filter(market => market !== marketToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) {
      toast.error('User profile not found');
      return;
    }

    setLoading(true);
    try {
      const prospectData = {
        ...formData,
        sales_rep_id: profile.id,
        geographic_markets: geographicMarkets,
        tags,
        next_action_date: nextActionDate?.toISOString().split('T')[0] || null,
        annual_event_volume: formData.annual_event_volume ? parseInt(formData.annual_event_volume) : null,
        average_participants_per_event: formData.average_participants_per_event ? parseInt(formData.average_participants_per_event) : null,
        estimated_value: formData.estimated_value ? parseInt(formData.estimated_value) : null,
        technology_readiness: parseInt(formData.technology_readiness.toString())
      };

      let error;
      if (prospect?.id) {
        const { error: updateError } = await supabase
          .from('white_label_prospects')
          .update(prospectData)
          .eq('id', prospect.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('white_label_prospects')
          .insert([prospectData]);
        error = insertError;
      }

      if (error) throw error;

      toast.success(prospect?.id ? 'Prospect updated successfully' : 'Prospect created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error saving prospect:', error);
      toast.error('Failed to save prospect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {prospect?.id ? 'Edit Prospect' : 'Add New White Label Prospect'}
          </DialogTitle>
          <DialogDescription>
            Enter the details for the event organizer prospect
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    value={formData.organization_name}
                    onChange={(e) => handleInputChange('organization_name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => handleInputChange('contact_name', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_type">Organization Type *</Label>
                <Select
                  value={formData.organization_type}
                  onValueChange={(value) => handleInputChange('organization_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high_school">High School</SelectItem>
                    <SelectItem value="club_league">Club League</SelectItem>
                    <SelectItem value="tournament_company">Tournament Company</SelectItem>
                    <SelectItem value="sports_association">Sports Association</SelectItem>
                    <SelectItem value="recreation_department">Recreation Department</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Event Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="annual_event_volume">Annual Event Volume</Label>
                  <Input
                    id="annual_event_volume"
                    type="number"
                    min="0"
                    value={formData.annual_event_volume}
                    onChange={(e) => handleInputChange('annual_event_volume', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="average_participants_per_event">Avg Participants per Event</Label>
                  <Input
                    id="average_participants_per_event"
                    type="number"
                    min="0"
                    value={formData.average_participants_per_event}
                    onChange={(e) => handleInputChange('average_participants_per_event', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Geographic Markets</Label>
                <div className="flex gap-2">
                  <Input
                    value={newMarket}
                    onChange={(e) => setNewMarket(e.target.value)}
                    placeholder="Add geographic market"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMarket())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddMarket}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {geographicMarkets.map((market, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {market}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleRemoveMarket(market)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sales Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Sales Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pipeline_stage">Pipeline Stage</Label>
                  <Select
                    value={formData.pipeline_stage}
                    onValueChange={(value) => handleInputChange('pipeline_stage', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="demo_requested">Demo Requested</SelectItem>
                      <SelectItem value="demo_completed">Demo Completed</SelectItem>
                      <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                      <SelectItem value="contract_negotiation">Contract Negotiation</SelectItem>
                      <SelectItem value="implementation">Implementation</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="renewal">Renewal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority_level">Priority Level</Label>
                  <Select
                    value={formData.priority_level}
                    onValueChange={(value) => handleInputChange('priority_level', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimated_value">Estimated Value ($)</Label>
                  <Input
                    id="estimated_value"
                    type="number"
                    min="0"
                    value={formData.estimated_value}
                    onChange={(e) => handleInputChange('estimated_value', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="technology_readiness">Technology Readiness (1-5)</Label>
                <Select
                  value={formData.technology_readiness.toString()}
                  onValueChange={(value) => handleInputChange('technology_readiness', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Very Low</SelectItem>
                    <SelectItem value="2">2 - Low</SelectItem>
                    <SelectItem value="3">3 - Medium</SelectItem>
                    <SelectItem value="4">4 - High</SelectItem>
                    <SelectItem value="5">5 - Very High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="next_action">Next Action</Label>
                  <Input
                    id="next_action"
                    value={formData.next_action}
                    onChange={(e) => handleInputChange('next_action', e.target.value)}
                    placeholder="e.g., Schedule demo call"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Action Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !nextActionDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {nextActionDate ? format(nextActionDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={nextActionDate}
                        onSelect={setNextActionDate}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_technology">Current Technology</Label>
                <Input
                  id="current_technology"
                  value={formData.current_technology}
                  onChange={(e) => handleInputChange('current_technology', e.target.value)}
                  placeholder="e.g., Excel spreadsheets, custom software"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="competition_analysis">Competition Analysis</Label>
                <Textarea
                  id="competition_analysis"
                  value={formData.competition_analysis}
                  onChange={(e) => handleInputChange('competition_analysis', e.target.value)}
                  placeholder="Analysis of competitors they're considering..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes and insights..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : prospect?.id ? 'Update Prospect' : 'Create Prospect'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WhiteLabelProspectForm;